import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTopSheet, useRevenueTrend } from '../../services/httpServices/queries';
import { PageHelp } from '../../components/ui/PageHelp';
import { KpiStatCard } from '../../components/ui/KpiStatCard';
import { PeriodPicker } from '../../components/ui/PeriodPicker';
import { describeFinancialPeriod, useFinancialPeriod } from '../../context/FinancialPeriodContext';
import { BILLING_METRIC_INFO } from '../../content/billingMetricInfo';
import {
  Loader2,
  AlertCircle,
  DollarSign,
  FileText,
  ClipboardList,
  Wallet,
  Receipt,
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Package,
  Bed,
  Pill,
  CreditCard,
  BookOpen,
  Handshake,
  AlertTriangle,
  ArrowDownLeft,
} from 'lucide-react';
import { DueCollectionsModal } from './DueCollectionsModal';
import { usePermissions } from '../../hooks/usePermissions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#0f766e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

const CURRENCY = '৳';

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
  other: 'Other',
};

export function Billing() {
  const navigate = useNavigate();
  const [dueModalOpen, setDueModalOpen] = useState(false);
  const { hasAccess } = usePermissions();
  // The Risk Fund tile (and its link to the ledger) is only meaningful to a
  // role that can open the ledger — i.e. an explicit `billing:risk-fund:*`
  // grant. Built-in finance roles do not get it by default.
  const canViewRiskFund = hasAccess('/billing/risk-fund');
  const { period } = useFinancialPeriod();
  const { data: sheetData, isLoading, error } = useTopSheet(
    period.from || undefined,
    period.to || undefined,
  );
  const { data: trendData } = useRevenueTrend(7);

  const sheet = sheetData?.data;
  const trend = trendData?.data?.trend || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load billing overview</span>
      </div>
    );
  }

  const paymentModeData = sheet.collections.byMethod.map((entry) => ({
    name: METHOD_LABELS[entry.method] || entry.method,
    value: entry.amount,
  }));

  const singleDayPeriod = Boolean(period.from && period.to && period.from === period.to);
  const currentDueLabel = singleDayPeriod ? 'Today Due' : 'Current-period Due';
  const due = sheet.dueCollections;

  const statGroups = [
    {
      key: 'income',
      title: 'Income',
      hint: 'Selected period · after discounts',
      cards: [
        {
          label: 'Total Bill',
          value: fmt(sheet.revenue.totals.net),
          sub: 'Bills + diagnostics + pharmacy',
          icon: TrendingUp,
          color: 'bg-primary/10 text-primary',
          info: BILLING_METRIC_INFO.totalIncome,
          breakdown: {
            description:
              'Income earned in the selected period after discounts. This is the same figure older reports called “net revenue”.',
            items: [
              { label: 'Patient bills (invoices)', value: fmt(sheet.revenue.invoices.net) },
              { label: 'Diagnostics', value: fmt(sheet.revenue.diagnostics.net) },
              { label: 'Pharmacy sales', value: fmt(sheet.revenue.pharmacy.net) },
              { label: 'Total bill', value: fmt(sheet.revenue.totals.net), emphasis: true },
            ],
            route: '/billing/top-sheet',
            linkLabel: 'Top Sheet',
          },
        },
        {
          label: 'Cash Collected',
          value: fmt(sheet.collections.total),
          sub: 'Actual money received',
          icon: DollarSign,
          color: 'bg-success/10 text-success',
          info: BILLING_METRIC_INFO.collected,
          breakdown: {
            description:
              'Actual money received in the period (cash, card, bank and insurance), by where the patient was treated. A due paid today counts today — not on the day the bill was raised. IPD advances are included in IPD Collected. The Cashbook mirrors the physical drawer (cash/card/UPI) only, so bank/insurance receipts are not in it.',
            items: [],
            sections: [
              {
                label: 'Collected by care setting',
                items: [
                  {
                    label: 'OPD collected',
                    value: fmt(sheet.collections.bySetting?.opd),
                    hint: 'Outpatient bills & diagnostics',
                  },
                  {
                    label: 'IPD collected',
                    value: fmt(sheet.collections.bySetting?.ipd),
                    hint: 'Inpatient bills, diagnostics & advances',
                  },
                  {
                    label: 'Pharmacy collected',
                    value: fmt(sheet.collections.bySetting?.pharmacy),
                    hint: 'Counter sales',
                  },
                ],
              },
            ],
            total: {
              label: 'Total Cash Collected',
              value: fmt(sheet.collections.total),
            },
            route: '/billing/payments',
            linkLabel: 'Payments',
          },
        },
        {
          label: 'Due Collected',
          value: fmt(due?.total?.total),
          sub: 'Money received against bills already raised',
          icon: ArrowDownLeft,
          color: 'bg-warning/10 text-warning',
          info: BILLING_METRIC_INFO.dueCollected,
          breakdown: {
            description:
              'The part of Cash Collected that settled an existing bill. “Today Due” is dues of bills raised in this period; “Old Due” is money collected now against bills raised before it. IPD advances and point-of-sale tenders are not dues.',
            items: [],
            sections: [
              {
                label: currentDueLabel,
                hint: 'Bills raised in this period',
                items: [
                  { label: 'IPD', value: fmt(due?.current?.ipd) },
                  { label: 'OPD', value: fmt(due?.current?.opd) },
                  { label: 'Pharmacy', value: fmt(due?.current?.pharmacy) },
                  {
                    label: `Total ${currentDueLabel}`,
                    value: fmt(due?.current?.total),
                    emphasis: true,
                  },
                ],
              },
              {
                label: 'Old Due',
                hint: 'Bills raised before this period',
                items: [
                  { label: 'IPD', value: fmt(due?.old?.ipd) },
                  { label: 'OPD', value: fmt(due?.old?.opd) },
                  { label: 'Pharmacy', value: fmt(due?.old?.pharmacy) },
                  {
                    label: 'Total Old Due',
                    value: fmt(due?.old?.total),
                    emphasis: true,
                  },
                ],
              },
            ],
            total: {
              label: 'Total Due Collected',
              value: fmt(due?.total?.total),
            },
            sectionColumns: 2 as const,
            actionLabel: 'View due collection list',
            onAction: () => setDueModalOpen(true),
          },
        },
      ],
    },
    {
      key: 'expenses',
      title: 'Expenses',
      hint: 'Selected period',
      cards: [
        {
          label: 'Total Expenses',
          value: fmt(sheet.expenses.total),
          sub: 'Referral pay + payroll + other costs',
          icon: TrendingDown,
          color: 'bg-danger/10 text-danger',
          info: BILLING_METRIC_INFO.totalExpenses,
          breakdown: {
            description:
              'Every cost for the selected period. Referral pay is a share of the income the hospital earned — a hospital cost, not separate income. Any unpaid part is also included in Total Payables.',
            items: [
              { label: 'Referral pay (all partner types)', value: fmt(sheet.expenses.referralShare) },
              { label: 'Payroll', value: fmt(sheet.expenses.payroll.net) },
              { label: 'Operating expenses', value: fmt(sheet.expenses.operating?.total) },
              { label: 'Fixed assets', value: fmt(sheet.expenses.assets?.total) },
              {
                label: 'Total expenses',
                value: fmt(sheet.expenses.total),
                emphasis: true,
              },
            ],
            route: '/billing/top-sheet?tab=financials',
            linkLabel: 'Profit & Loss',
          },
        },
        {
          label: 'Referral Pay',
          value: fmt(sheet.expenses.referralShare),
          sub: 'Doctors, employees & outside partners',
          icon: Handshake,
          color: 'bg-secondary/10 text-secondary',
          info: BILLING_METRIC_INFO.referralShare,
          breakdown: {
            description:
              'Commission paid to registered referral partners for sending patients. A referral partner can be a doctor, an employee or an outside agent — all are paid from this same Referral Pay ledger. The unpaid part is a payable, not extra expense.',
            items: [
              { label: 'Referral commission earned', value: fmt(sheet.expenses.referralShare) },
              {
                label: 'Doctor share',
                value: fmt(sheet.expenses.referralShareByType?.doctor?.share),
                hint: `${sheet.expenses.referralShareByType?.doctor?.partnerCount || 0} partner(s)`,
              },
              {
                label: 'Employee share',
                value: fmt(sheet.expenses.referralShareByType?.employee?.share),
                hint: `${sheet.expenses.referralShareByType?.employee?.partnerCount || 0} partner(s)`,
              },
              {
                label: 'Other share',
                value: fmt(sheet.expenses.referralShareByType?.other?.share),
                hint: `${sheet.expenses.referralShareByType?.other?.partnerCount || 0} partner(s)`,
              },
              {
                label: 'Not yet paid to partners',
                value: fmt(sheet.payables.referrals.outstanding),
                hint: `${sheet.payables.referrals.partnerCount} partner(s) · shown under Total Payables`,
              },
              { label: 'Already settled', value: fmt(sheet.payables.referrals.settled) },
            ],
            route: '/billing/payouts?tab=outstanding',
            linkLabel: 'Referral Payouts',
          },
        },
        {
          label: 'Payroll',
          value: fmt(sheet.expenses.payroll.net),
          sub: `${sheet.expenses.payroll.count} record(s)`,
          icon: CreditCard,
          color: 'bg-primary/10 text-primary',
          info: BILLING_METRIC_INFO.payroll,
          breakdown: {
            description: 'Net salary expense for the period.',
            items: [
              { label: 'Gross salary', value: fmt(sheet.expenses.payroll.gross) },
              { label: 'Paid', value: fmt(sheet.expenses.payroll.paid) },
              { label: 'Pending', value: fmt(sheet.expenses.payroll.pending) },
              {
                label: 'Net salary',
                value: fmt(sheet.expenses.payroll.net),
                hint: `${sheet.expenses.payroll.count} record(s)`,
                emphasis: true,
              },
            ],
            route: '/hr?tab=payroll',
            linkLabel: 'Payroll',
          },
        },
        {
          label: 'Operating Expenses',
          value: fmt(sheet.expenses.operating?.total),
          sub: `${sheet.expenses.operating?.count || 0} record(s) · rent, utilities, supplies`,
          icon: Wallet,
          color: 'bg-warning/10 text-warning',
          info: BILLING_METRIC_INFO.operatingExpenses,
          breakdown: {
            description: 'Day-to-day running expenses for the period.',
            items: [
              { label: 'Paid', value: fmt(sheet.expenses.operating?.paid) },
              { label: 'Pending', value: fmt(sheet.expenses.operating?.pending) },
              {
                label: 'Total operating expenses',
                value: fmt(sheet.expenses.operating?.total),
                hint: `${sheet.expenses.operating?.count || 0} record(s)`,
                emphasis: true,
              },
            ],
            route: '/billing/expenses',
            linkLabel: 'Expenses',
          },
        },
        {
          label: 'Fixed Assets',
          value: fmt(sheet.expenses.assets?.total),
          sub: `${sheet.expenses.assets?.count || 0} purchase(s) · equipment, furniture`,
          icon: Package,
          color: 'bg-primary/10 text-primary',
          info: BILLING_METRIC_INFO.fixedAssets,
          breakdown: {
            description: 'Capital spent on fixed assets in the period.',
            items: [
              {
                label: 'Total asset spend',
                value: fmt(sheet.expenses.assets?.total),
                hint: `${sheet.expenses.assets?.count || 0} purchase(s)`,
                emphasis: true,
              },
            ],
            route: '/billing/assets',
            linkLabel: 'Assets',
          },
        },
      ],
    },
    {
      key: 'outstanding',
      title: 'Outstanding',
      hint: 'All-time position · not limited to the selected period',
      cards: [
        {
          label: 'Patient Dues',
          value: fmt(sheet.receivables.total),
          sub: 'Unpaid patient bills',
          icon: Receipt,
          color: 'bg-warning/10 text-warning',
          info: BILLING_METRIC_INFO.patientDues,
          breakdown: {
            description:
              'Money patients still owe for services already provided. This is a running all-time balance, so it is not limited to the selected period. Older reports call this “receivables”.',
            items: [
              { label: 'Invoices due', value: fmt(sheet.receivables.invoices) },
              { label: 'Diagnostics due', value: fmt(sheet.receivables.diagnostics) },
              { label: 'Pharmacy due', value: fmt(sheet.receivables.pharmacy) },
              { label: 'Total patient dues', value: fmt(sheet.receivables.total), emphasis: true },
            ],
            route: '/billing/invoices?status=due',
            linkLabel: 'Invoices',
          },
        },
        ...(canViewRiskFund
          ? [
              {
                label: 'Risk Fund',
                value: fmt(sheet.riskFund?.total),
                sub: 'Uncollectible patient balances',
                icon: AlertTriangle,
                color: 'bg-danger/10 text-danger',
                info: BILLING_METRIC_INFO.riskFund,
                breakdown: {
                  description:
                    'Amounts moved out of active patient dues because they are deemed uncollectible (e.g., patient abandoned, deceased, or unable to pay). These are no longer chased but remain recorded for audit.',
                  items: [
                    { label: 'OPD Risk Fund', value: fmt(sheet.riskFund?.opd) },
                    { label: 'IPD Risk Fund', value: fmt(sheet.riskFund?.ipd) },
                    { label: 'Total Risk Fund', value: fmt(sheet.riskFund?.total), emphasis: true },
                  ],
                  route: '/billing/risk-fund',
                  linkLabel: 'Risk Fund',
                },
              },
            ]
          : []),
        {
          label: 'Total Payables',
          value: fmt(sheet.payables.total),
          sub: 'Money the hospital owes · all time',
          icon: Wallet,
          color: 'bg-danger/10 text-danger',
          info: BILLING_METRIC_INFO.totalPayables,
          breakdown: {
            description:
              'Money the hospital still owes to referral partners and suppliers. The referral portion is the unpaid part of shares already counted under Expenses, so it is a liability — never a second income or expense.',
            items: [
              {
                label: 'Referral partners',
                value: fmt(sheet.payables.referrals.outstanding),
                hint: `${sheet.payables.referrals.partnerCount} partner(s)`,
              },
              {
                label: 'Pharmacy suppliers',
                value: fmt(sheet.payables.pharmacy.outstanding),
                hint: `${fmt(sheet.payables.pharmacy.overdue)} overdue · ${sheet.payables.pharmacy.invoiceCount} invoice(s)`,
              },
              {
                label: 'Asset vendors',
                value: fmt(sheet.payables.assetVendors?.outstanding),
                hint: `${fmt(sheet.payables.assetVendors?.overdue)} overdue · ${sheet.payables.assetVendors?.invoiceCount || 0} invoice(s)`,
              },
              { label: 'Total payables', value: fmt(sheet.payables.total), emphasis: true },
            ],
            route: '/billing/payouts',
            linkLabel: 'Payouts',
          },
        },
      ],
    },
  ];

  const quickActions = [
    { label: 'Top Sheet', route: '/billing/top-sheet', icon: LayoutDashboard },
    { label: 'Invoices', route: '/billing/invoices', icon: FileText },
    { label: 'Record Payment', route: '/billing/payments', icon: DollarSign },
    { label: 'Cashbook', route: '/billing/cashbook', icon: BookOpen },
    { label: 'Payouts', route: '/billing/payouts', icon: Wallet },
    { label: 'Referral Partners', route: '/referrals', icon: Handshake },
    { label: 'Assets', route: '/billing/assets', icon: Package },
    { label: 'Asset Categories', route: '/billing/assets/categories', icon: Package },
    { label: 'Expense Categories', route: '/billing/expenses/categories', icon: Package },
    { label: 'Expense Vendors', route: '/billing/expenses/vendors', icon: Package },
    { label: 'Expenses', route: '/billing/expenses', icon: Wallet },
    { label: 'Record Expense', route: '/billing/expenses/new', icon: Wallet },
    { label: 'Diagnostic Receipts', route: '/opd/diagnostic-receipts', icon: ClipboardList },
    { label: 'IPD Admissions', route: '/ipd', icon: Bed },
    { label: 'Pharmacy Sales', route: '/pharmacy', icon: Pill },
    { label: 'Pharmacy Payables', route: '/pharmacy/payables', icon: Package },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">Billing</h1>
              <PageHelp path="/billing" />
            </div>
            <p className="text-xs text-text-muted">
              Consolidated position · {describeFinancialPeriod(period)}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/billing/top-sheet')}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <LayoutDashboard size={16} />
          Open Top Sheet
        </button>
      </div>

      {/* Shared reporting period (kept in sync with the Top Sheet) */}
      <PeriodPicker />

      {/* KPI Cards — grouped so income, expenses and outstanding balances are
          never mixed together. Details expand inside each card (no modals). */}
      {statGroups.map((group) => (
        <section key={group.key} className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-lg font-semibold text-text">{group.title}</h2>
            <p className="text-xs text-text-muted">{group.hint}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.cards.map((stat) => (
              <KpiStatCard
                key={stat.label}
                label={stat.label}
                value={stat.value}
                sub={stat.sub}
                icon={stat.icon}
                color={stat.color}
                info={stat.info}
                breakdown={stat.breakdown}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        {quickActions.map((action) => (
          <button
            key={action.label}
            onClick={() => navigate(action.route)}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
          >
            <action.icon size={16} />
            {action.label}
          </button>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Trend */}
        <div className="bg-surface p-4 rounded-xl border border-border">
          <h3 className="text-lg font-semibold text-text mb-4">Income Trend (Last 7 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 13 }} />
                <YAxis tick={{ fontSize: 13 }} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#0f766e" strokeWidth={2} dot={{ fill: '#0f766e' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Mode Breakdown */}
        <div className="bg-surface p-4 rounded-xl border border-border">
          <h3 className="text-lg font-semibold text-text mb-4">Collections by Payment Mode</h3>
          <div className="h-64">
            {paymentModeData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-text-muted text-sm">
                No payment data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentModeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                  >
                    {paymentModeData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => fmt(value)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          {paymentModeData.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              {paymentModeData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-1.5 text-xs text-text-muted">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                  {entry.name}: {fmt(entry.value)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {dueModalOpen && (
        <DueCollectionsModal
          periodLabel={describeFinancialPeriod(period)}
          dateFrom={period.from || undefined}
          dateTo={period.to || undefined}
          currentLabel={currentDueLabel}
          outstandingDue={sheet.receivables.total}
          onClose={() => setDueModalOpen(false)}
        />
      )}
    </div>
  );
}
